import { Adjustments, ColorProfileName, Curve, LookTable } from "@/core/types"
import {
	ensureValidFaceData,
	gatherRGBValues,
	getInputColorsAndWeights,
	getLightingLimits,
	getWeightedColors,
	getWeightedMeanRgb,
	getWeightedRefFeatures,
	ColorMatchRuntimeInfo,
	InputColorMatchFeaturesWithFaceData,
	isAllNearZero,
	isAnyNaNorInf,
	isNearlyBW,
	LightingLimits,
	ReferenceColorMatchFeatures,
	ReferenceItem,
	reportDegenerateColorMatchInputs,
} from "./utils"
import { NeuralColorMatchResult } from "./neural-color-match"
import { getMergedLut, transformImage } from "./lut-utils"
import { vec2, vec3 } from "gl-matrix"
import {
	applyTransform,
	applyTransformToOneDimension,
	linearToGamma,
	prophotoToSrgb,
	rgbToSat,
	srgbToProphoto,
} from "./color-space"
import {
	getMean,
	xyz_to_lms,
	lms_to_xyz,
	srgb_to_xyz,
	meanAbsoluteDifference,
} from "../calibration/ai-calibration-utils"
import { getClampedWBBias } from "./utils"
import {
	renderCpu,
	ColorSpaceCpu,
	AsShotWB,
	tonemapInv,
	RenderOptions,
	renderInverseCurveProPhoto,
	srgbToToneMapInversed,
} from "./renderer-cpu"
import { clamp } from "@/utils/math"
import { illuminants } from "@/core/utils/color"
import { whiteBalanceToXY, XYtoWhiteBalance } from "@/core/utils/temperature"
import { XYtoXYZ, XYZtoXY } from "@/core/utils/color-conversion"
import {
	adjustForLowSatRef,
	getCalibration,
	getHSL,
	maintainOrangeRedSaturation,
	reduceHighHSLSaturation,
} from "./hsl"
import {
	adjustFaceTone,
	adjustFaceToneToLutResult,
	getFaceHSVL,
	isFaceProblematic,
} from "./face-tone"
import { getGrayColors, getPointColors, getWhiteColors } from "./point-color"
import { getColorGrading } from "./color-grading"
import {
	getAutoHighlights,
	getAutoHighlightsFromPercentiles,
	getDecreasedValue,
	getGrayImage,
	getLuminancePercentiles,
	getMeanAbsDifference,
	getMeanDifference,
	getWeightedDiff,
	getWeightedGrayDiff,
	getWeightedMean,
	getWeightedMedianLuminance,
	getWeightedToneDiff,
	logColorMatch,
	step,
} from "./utils"
import { getValidProfileCurve, getCurves, softenCurveUpperMid } from "./curve"
import { getAdjustmentRange } from "@/core/adjustments"
import { getAutoExposure } from "../calibration/ai-calibration-brightness"
import { hashObject } from "@/utils/hash"
import { correctVegetation, getWbRange } from "./vegetation-color"
import { getInverseDistanceWeights, getWeightedSum } from "../calibration/ai-calibration-feature"
import { Sentry } from "@/telemetry/sentry"

// Boolean flags for color match parameter estimation
export const enableVegetationCorrection = true
const enableWhiteColorCorrection = true
const enableGrayColorCorrection = true
export const enableHighlightCorrection = true
const enablePointColors = false

// Add a cache for renderCpu results
const renderCache: Map<string, Float32Array> = new Map()

function cachedRenderCpu(
	sourceImage: Float32Array,
	adjustments: Partial<Adjustments>,
	options: RenderOptions,
) {
	const simpleOptions = {
		inputColorSpace: options.inputColorSpace,
		outColorSpace: options.outColorSpace,
		asShotTemp: options.asShotTemp,
		asShotTint: options.asShotTint,
		hasProfileCurve: options.profileCurve != undefined,
		hasColorLookTable: options.colorLookTable != undefined,
		hasProfileLookTable: options.profileLookTable != undefined,
	}
	const key = hashObject({ ...adjustments, ...simpleOptions, imageSize: sourceImage.length })
	if (renderCache.has(key)) {
		return renderCache.get(key) as Float32Array
	}

	const result = renderCpu(sourceImage, adjustments, options)
	renderCache.set(key, result)
	return result
}

/**
 * Multi-reference variant of the neural color match parameter estimator (K>1 path).
 * Each reference's neural LUTs are applied independently, then the resulting color/gray/face
 * pixel arrays are blended via inverse-distance weights before a single `estimateParameters`
 * call produces the final adjustment set. Reference features are also distance-weighted so
 * downstream HSL / face-tone corrections see a consistent blended reference.
 *
 * @param inputImageBitmap - Decoded bitmap of the photo being processed (used to sample colors).
 * @param asShotWB - Camera as-shot white balance (temp + tint) applied during CPU rendering.
 * @param profileData - Camera profile data (tone curve + 3D LUTs) for accurate color rendering.
 * @param inputFeatures - Pre-computed neural input features for this photo, including face data.
 * @param baseAdjustments - Existing adjustments on the photo used as the starting point for
 *   incremental estimation; pass undefined to estimate from scratch (computeFullParams path).
 * @param computeFullParams - True to estimate the full adjustment set ignoring baseAdjustments;
 *   false to estimate a delta on top of the existing adjustments (half color match path -
 *   when preset is provided or member photo has key photo's adjustments as base).
 * @param computeWb - True to estimate white balance from the reference; false when the key photo
 *   already provided WB and re-computing it would cause inconsistency.
 * @param outputLut - Reserved for future use (not yet consumed by this function).
 * @param keyIsRef - True when the photo's key neighbor is itself a reference photo; disables
 *   highlight correction when base adjustments is the same as the reference photo's final adjustments.
 * @param references - Ordered list of reference items (each carrying pre-loaded LUTs, webp bitmap
 *   cache key, colorStrength, featureDist, and imageWbDist) from which blending weights are derived.
 * @returns Object with `adjustmentsSkinToneOptimized` — the final blended + face-corrected adjustments.
 */
export function estimateNeuralColorMatchParamsMultiRef(options: {
	inputImageBitmap: ImageBitmap
	asShotWB: AsShotWB
	profileData: {
		profileCurve: Curve | null
		colorLookTable: LookTable | null
		profileLookTable: LookTable | null
		userLookTable: LookTable | null
	}
	inputFeatures: InputColorMatchFeaturesWithFaceData
	baseAdjustments?: Partial<Adjustments>
	computeFullParams: boolean
	computeWb: boolean
	outputLut?: boolean
	keyIsRef?: boolean
	references: ReferenceItem[]
}): {
	adjustmentsSkinToneOptimized: Partial<Adjustments>
	runtimeInfo: ColorMatchRuntimeInfo // for debug and analysis only
} {
	const {
		inputImageBitmap,
		asShotWB,
		inputFeatures,
		baseAdjustments,
		keyIsRef,
		computeFullParams,
		references,
		computeWb,
	} = options
	const profileCurve = getValidProfileCurve(options.profileData.profileCurve)
	const baseRenderOptions = {
		...asShotWB,
		profileCurve,
		colorLookTable: options.profileData.colorLookTable,
		profileLookTable: options.profileData.profileLookTable,
		userLut: options.profileData.userLookTable,
	}

	renderCache.clear() // clear cache for each new photo

	ensureValidFaceData(inputFeatures)

	const {
		faceBefore,
		faceColorsWithPreset,
		faceWeights,
		inputBefore,
		colorsWithPreset,
		pixelWeights,
		grayBandBefore,
		grayWithPreset,
	} = getInputColorsAndWeights({
		inputFeatures,
		baseAdjustments,
		inputImageBitmap,
		baseRenderOptions,
	})

	// Decide weights for each reference by computing the color difference to the input photo
	const distances: number[] = []
	const inputAfters: Float32Array[] = []
	const faceAfters: Float32Array[] = []
	const grayBandAfters: Float32Array[] = []
	const whiteAreaAfters: Float32Array[] = []
	const colorStrengths: number[] = []
	const lightStrengths: number[] = []
	const runtimeInfo: ColorMatchRuntimeInfo = {
		algorithm: "multi",
		references: [],
	}
	for (const reference of references) {
		const luts = reference.luts as NeuralColorMatchResult

		ensureValidFaceData(inputFeatures, reference.referenceFeatures)

		// Get color strength and light strength
		const colorStrength = reference.colorStrength ?? 1
		const lightStrength = getLightStrength({
			inputFeatures,
			referenceFeatures: reference.referenceFeatures,
			colorStrength,
			faceColorsWithPreset,
			luts,
		})
		colorStrengths.push(colorStrength)
		lightStrengths.push(lightStrength)

		// Transform colors using luts
		const inputAfter = transformImage({
			image: colorsWithPreset,
			lut1d: luts.lut1d,
			lut3d: luts.lut3d,
			colorStrength,
			lightStrength,
			originalInput: baseAdjustments ? inputBefore : undefined,
		})
		inputAfters.push(inputAfter)

		const diffL1 = getWeightedDiff(inputAfter, colorsWithPreset, pixelWeights)
		const diffTone = getWeightedToneDiff(inputAfter, colorsWithPreset, pixelWeights)
		const distance =
			diffL1 +
			diffTone +
			Math.max(0, reference.featureDist - 0.1) * 0.6 +
			clamp(reference.imageWbDist, 0, 0.1) * 0.3
		logColorMatch("[multi-ref] distance", {
			refId: reference.referenceId,
			distance,
			diffL1,
			diffTone,
			featureDist: reference.featureDist,
			wbDist: reference.imageWbDist,
		})
		distances.push(distance)
		runtimeInfo.references.push({
			referenceId: reference.referenceFeatures.imageId,
			blendWeight: 0,
			runtimeDistance: distance,
			colorStrength,
			lightStrength,
		})

		const faceAfter =
			faceColorsWithPreset && faceBefore
				? transformImage({
						image: faceColorsWithPreset,
						lut1d: luts.lut1d,
						lut3d: luts.lut3d,
						colorStrength,
						lightStrength,
						originalInput: baseAdjustments ? faceBefore : undefined,
					})
				: null
		if (faceAfter) {
			faceAfters.push(faceAfter)
		}

		const grayBandAfter = transformImage({
			image: grayWithPreset,
			lut1d: luts.lut1d,
			lut3d: luts.lut3d,
			colorStrength,
			lightStrength,
			originalInput: baseAdjustments ? grayBandBefore : undefined,
		})
		grayBandAfters.push(grayBandAfter)

		let whiteAreaAfter: Float32Array = grayBandAfter.slice(180 * 3, 256 * 3)
		if (computeWb) {
			const whiteAreaBefore = grayBandBefore.slice(180 * 3, 256 * 3)
			whiteAreaAfter = transformImage({
				// effects of the LUT only to get WB
				image: whiteAreaBefore,
				lut1d: luts.lut1d,
				lut3d: luts.lut3d,
				colorStrength,
				lightStrength,
				originalInput: baseAdjustments ? whiteAreaBefore : undefined,
			})
		}
		whiteAreaAfters.push(whiteAreaAfter)
	}
	const weights = getInverseDistanceWeights(distances, 0.5)
	logColorMatch("[multi-ref] weights", {
		weights,
		distances,
	})
	runtimeInfo.references = runtimeInfo.references.map((reference, index) => ({
		...reference,
		blendWeight: weights[index] ?? 0,
	}))

	// Get weighted sum of input afters and reference features
	const inputAfter = getWeightedColors(inputAfters, weights)
	const faceAfter = faceAfters.length > 0 ? getWeightedColors(faceAfters, weights) : null
	const grayBandAfter = getWeightedColors(grayBandAfters, weights)
	const whiteAreaAfter = getWeightedColors(whiteAreaAfters, weights)
	const colorStrength = getWeightedSum(colorStrengths, weights)
	const lightStrength = getWeightedSum(lightStrengths, weights)
	const referenceFeatures = getWeightedRefFeatures(
		references.map((r) => r.referenceFeatures),
		weights,
		inputFeatures.avgFaceHsvl,
		references.map((r) => r.imageWbDist),
	)

	// Don't apply highlight correction for if the key photo is a reference photo (base adjustments is the same as the reference photo's adjustments)
	const enableHighlightCorrectionForThisPhoto = enableHighlightCorrection && !keyIsRef
	const maxExposure = enableHighlightCorrectionForThisPhoto
		? getMaxExposure({ baseAdjustments, maxExposure: inputFeatures.maxExposure })
		: 5
	logColorMatch("[multi-ref] inputs", {
		inputFeatures,
		referenceFeatures,
		asShotWB,
		profileData: options.profileData,
		size: [inputImageBitmap.width, inputImageBitmap.height],
		maxExposure,
		baseAdjustments: structuredClone(baseAdjustments),
		colorStrength,
		lightStrength,
		computeWb,
	})

	const baseWb = { temperature: baseAdjustments?.temperature, tint: baseAdjustments?.tint }

	reportDegenerateColorMatchInputs({
		algorithm: "multi",
		inputBefore,
		inputWithPreset: colorsWithPreset,
		inputAfter,
		asShotWB,
		hasBaseAdjustments: baseAdjustments != null,
		computeWb,
	})

	// Estimate parameters main function
	const initialAdjustments = estimateParameters({
		graySrgbBefore: grayBandBefore,
		graySrgbAfter: grayBandAfter,
		srgbBefore: inputBefore,
		srgbAfter: inputAfter,
		whiteAreaAfter,
		pixelWeights,
		faceAfter,
		asShotWB,
		baseRenderOptions,
		inputFeatures,
		referenceFeatures,
		baseAdjustments: computeFullParams ? undefined : baseAdjustments,
		computeWb,
		baseWb,
		maxExposure,
		enableHighlightCorrectionForThisPhoto,
		featureDist: references[0]?.featureDist ?? 0,
	})

	// Some following functions depend on baseAdjustments to determine if computeWb is true
	if (computeWb && baseAdjustments) {
		delete baseAdjustments.temperature
		delete baseAdjustments.tint
	}

	let hslScale = 1
	if (!computeWb && !enableHighlightCorrectionForThisPhoto) {
		hslScale = 0.5 // less hsl adjustment for members in a cluster where the key photo is a reference
		// also less saturation adjustment
		initialAdjustments.saturation =
			((initialAdjustments.saturation ?? 0) - (baseAdjustments?.saturation ?? 0)) * hslScale +
			(baseAdjustments?.saturation ?? 0)
	}

	const satCorrectionOptions = {
		baseAdjustments: computeFullParams ? undefined : baseAdjustments,
		inputPreset: colorsWithPreset,
		inputAfter,
		//luts,
		lightStrength,
		colorStrength,
		pixelWeights,
	}

	let adjustmentsSkinToneOptimized = adjustFaceTone({
		inputFeatures: inputFeatures,
		referenceFeatures: referenceFeatures,
		adjustments: initialAdjustments,
		baseRenderOptions,
		faceBefore,
		faceAfter,
		faceWeights,
		hslScale,
		srgbBefore: inputBefore,
		maxExposure,
		satCorrectionOptions,
	})

	// for no face photo, mix with auto exposure if reference photo is very dark or bright
	if (baseAdjustments && lightStrength < 1 && !inputFeatures.avgFaceHsvl) {
		const isRefDark = referenceFeatures.satLumStats.totalAvgLum < 0.35

		adjustmentsSkinToneOptimized = adjustWithAutoLightParams({
			adjustments: adjustmentsSkinToneOptimized,
			inputBefore,
			pixelWeights,
			baseRenderOptions,
			lightStrength,
			isDark: isRefDark,
		})
	}

	logColorMatch("!!estimated adjustments", {
		initialAdjustments,
		adjustmentsSkinToneOptimized,
	})

	return { adjustmentsSkinToneOptimized, runtimeInfo }
}

/**
 * Single-reference neural color match parameter estimator (K=1 path).
 * Applies the reference's neural 1D+3D LUTs to the input photo's sampled color, gray-band, and
 * face pixels, then calls `estimateParameters` to invert the color shift into concrete adjustment
 * values (exposure, WB, HSL, curves, color grading, etc.). Runs entirely on CPU in the AI worker.
 *
 * @param inputImageBitmap - Decoded bitmap of the photo being processed (used to sample colors).
 * @param luts - Pre-computed 1D and 3D neural LUTs for the reference photo.
 * @param asShotWB - Camera as-shot white balance (temp + tint) applied during CPU rendering.
 * @param profileData - Camera profile data (tone curve + 3D LUTs) for accurate color rendering.
 * @param inputFeatures - Pre-computed neural input features for this photo, including face data.
 * @param referenceFeatures - Color match features extracted from the reference photo's edited render.
 * @param baseAdjustments - Existing adjustments on the photo used as the starting point for
 *   incremental estimation; pass undefined to estimate from scratch (computeFullParams path).
 * @param computeFullParams - True to estimate the full adjustment set ignoring baseAdjustments;
 *   false to estimate a delta on top of the existing adjustments (half color match path -
 *   when preset is provided or member photo has key photo's adjustments as base).
 * @param computeWb - True to estimate white balance from the reference; false when the key photo
 *   already provided WB and re-computing it would cause inconsistency.
 * @param outputLut - If true, also returns `mergedLut3d` — the 3D LUT after merging reference and
 *   profile LUTs (used for debug visualisation).
 * @param outputParamsWithoutFaceOptimization - If true, also returns `adjustments` — the
 *   adjustment set before face-tone correction (used for debug / comparison).
 * @param colorStrength - Blend factor (0–1) that scales how aggressively LUT colors are applied;
 *   defaults to 1 when omitted.
 * @param featureDist - Raw feature distance to the reference, forwarded to `estimateParameters`
 *   to gate certain corrections; defaults to a colorStrength-derived estimate when omitted.
 * @param keyIsRef - True when the photo's key neighbor is itself the reference photo; disables
 *   highlight correction when base adjustments is the same as the reference photo's final adjustments.
 * @returns Object with `adjustmentsSkinToneOptimized` (always), `adjustments` (if
 *   outputParamsWithoutFaceOptimization), and `mergedLut3d` (if outputLut).
 */
export function estimateNeuralColorMatchParams(options: {
	inputImageBitmap: ImageBitmap
	luts: NeuralColorMatchResult
	asShotWB: AsShotWB
	profileData: {
		profileCurve: Curve | null
		colorLookTable: LookTable | null
		profileLookTable: LookTable | null
		userLookTable: LookTable | null
	}
	inputFeatures: InputColorMatchFeaturesWithFaceData
	referenceFeatures: ReferenceColorMatchFeatures
	baseAdjustments?: Partial<Adjustments>
	computeFullParams: boolean
	computeWb: boolean
	outputLut?: boolean
	outputParamsWithoutFaceOptimization?: boolean
	colorStrength?: number
	featureDist?: number
	keyIsRef?: boolean
}): {
	adjustments: Partial<Adjustments> | null
	adjustmentsSkinToneOptimized: Partial<Adjustments>
	mergedLut3d: Float32Array
	runtimeInfo: ColorMatchRuntimeInfo
} {
	const {
		inputImageBitmap,
		luts,
		asShotWB,
		inputFeatures,
		referenceFeatures,
		outputLut,
		baseAdjustments,
		keyIsRef,
		outputParamsWithoutFaceOptimization,
		computeFullParams,
		computeWb,
	} = options
	const profileCurve = getValidProfileCurve(options.profileData.profileCurve)
	const baseRenderOptions = {
		...asShotWB,
		profileCurve,
		colorLookTable: options.profileData.colorLookTable,
		profileLookTable: options.profileData.profileLookTable,
		userLut: options.profileData.userLookTable,
	}

	renderCache.clear() // clear cache for each new photo

	ensureValidFaceData(inputFeatures, referenceFeatures)

	const {
		faceBefore,
		faceColorsWithPreset,
		faceWeights,
		inputBefore,
		colorsWithPreset,
		pixelWeights,
		grayBandBefore,
		grayWithPreset,
	} = getInputColorsAndWeights({
		inputFeatures,
		baseAdjustments,
		inputImageBitmap,
		baseRenderOptions,
	})

	// Get color strength and light strength
	const colorStrength = options.colorStrength ?? 1
	const lightStrength = getLightStrength({
		inputFeatures,
		referenceFeatures,
		colorStrength,
		faceColorsWithPreset,
		luts,
	})

	const runtimeInfo: ColorMatchRuntimeInfo = {
		algorithm: "single",
		references: [
			{
				referenceId: referenceFeatures.imageId,
				blendWeight: 1,
				colorStrength,
				lightStrength,
			},
		],
	}

	// Transform colors using luts
	const inputAfter = transformImage({
		image: colorsWithPreset,
		lut1d: luts.lut1d,
		lut3d: luts.lut3d,
		colorStrength,
		lightStrength,
		originalInput: baseAdjustments ? inputBefore : undefined,
	})
	const faceAfter =
		faceColorsWithPreset && faceBefore
			? transformImage({
					image: faceColorsWithPreset,
					lut1d: luts.lut1d,
					lut3d: luts.lut3d,
					colorStrength,
					lightStrength,
					originalInput: baseAdjustments ? faceBefore : undefined,
				})
			: null
	const grayBandAfter = transformImage({
		image: grayWithPreset,
		lut1d: luts.lut1d,
		lut3d: luts.lut3d,
		colorStrength,
		lightStrength,
		originalInput: baseAdjustments ? grayBandBefore : undefined,
	})

	let whiteAreaAfter: Float32Array = grayBandAfter.slice(180 * 3, 256 * 3)
	if (computeWb) {
		const whiteAreaBefore = grayBandBefore.slice(180 * 3, 256 * 3)
		whiteAreaAfter = transformImage({
			// effects of the LUT only to get WB
			image: whiteAreaBefore,
			lut1d: luts.lut1d,
			lut3d: luts.lut3d,
			colorStrength,
			lightStrength,
			originalInput: baseAdjustments ? whiteAreaBefore : undefined,
		})
	}

	// Don't apply highlight correction for if the key photo is a reference photo (base adjustments is the same as the reference photo's adjustments)
	const enableHighlightCorrectionForThisPhoto = enableHighlightCorrection && !keyIsRef
	const maxExposure = enableHighlightCorrectionForThisPhoto
		? getMaxExposure({ baseAdjustments, maxExposure: inputFeatures.maxExposure })
		: 5
	logColorMatch("!!inputs", {
		inputFeatures,
		referenceFeatures,
		asShotWB,
		profileData: options.profileData,
		size: [inputImageBitmap.width, inputImageBitmap.height],
		maxExposure,
		baseAdjustments: structuredClone(baseAdjustments),
		colorStrength,
		lightStrength,
		computeWb,
	})

	const baseWb = { temperature: baseAdjustments?.temperature, tint: baseAdjustments?.tint }

	reportDegenerateColorMatchInputs({
		algorithm: "single",
		inputBefore,
		inputWithPreset: colorsWithPreset,
		inputAfter,
		asShotWB,
		hasBaseAdjustments: baseAdjustments != null,
		computeWb,
	})

	// Estimate parameters main function
	const initialAdjustments = estimateParameters({
		graySrgbBefore: grayBandBefore,
		graySrgbAfter: grayBandAfter,
		srgbBefore: inputBefore,
		srgbAfter: inputAfter,
		whiteAreaAfter,
		pixelWeights,
		faceAfter,
		asShotWB,
		baseRenderOptions,
		inputFeatures,
		referenceFeatures,
		baseAdjustments: computeFullParams ? undefined : baseAdjustments,
		baseWb,
		computeWb,
		maxExposure,
		enableHighlightCorrectionForThisPhoto,
		featureDist: options.featureDist ?? (1 - colorStrength) * 0.4 + 0.2,
	})

	// Some following functions depend on baseAdjustments to determine if computeWb is true
	if (computeWb && baseAdjustments) {
		delete baseAdjustments.temperature
		delete baseAdjustments.tint
	}

	let hslScale = 1
	if (!computeWb && !enableHighlightCorrectionForThisPhoto) {
		hslScale = 0.5 // less hsl adjustment for members in a cluster where the key photo is a reference
		// also less saturation adjustment
		initialAdjustments.saturation =
			((initialAdjustments.saturation ?? 0) - (baseAdjustments?.saturation ?? 0)) * hslScale +
			(baseAdjustments?.saturation ?? 0)
	}

	const satCorrectionOptions = {
		baseAdjustments: computeFullParams ? undefined : baseAdjustments,
		inputPreset: colorsWithPreset,
		inputAfter,
		luts,
		lightStrength,
		colorStrength,
		pixelWeights,
	}

	let adjustmentsSkinToneOptimized = adjustFaceTone({
		inputFeatures: inputFeatures,
		referenceFeatures: referenceFeatures,
		adjustments: initialAdjustments,
		baseRenderOptions,
		faceBefore,
		faceAfter,
		faceWeights,
		hslScale,
		srgbBefore: inputBefore,
		maxExposure,
		satCorrectionOptions,
	})

	// only if outputParamsWithoutFaceOptimization is true, we process the adjustments without face optimization
	let adjustments: Partial<Adjustments> | null = null
	if (outputParamsWithoutFaceOptimization) {
		adjustments = initialAdjustments
		if (faceBefore?.length && faceWeights?.length && faceAfter?.length) {
			const res = adjustFaceToneToLutResult({
				faceBefore,
				faceWeights,
				faceAfter,
				adjustValue: true,
				adjustments: initialAdjustments,
				baseRenderOptions,
			})
			adjustments = res.result ?? initialAdjustments
		}
		adjustments = applyCorrection({
			adjustments,
			referenceFeatures,
			baseRenderOptions,
			srgbBefore: inputBefore,
			...satCorrectionOptions,
		})
	}

	// for no face photo, mix with auto exposure if reference photo is very dark or bright
	if (baseAdjustments && lightStrength < 1 && !inputFeatures.avgFaceHsvl) {
		const isRefDark = referenceFeatures.satLumStats.totalAvgLum < 0.35

		if (adjustments) {
			adjustments = adjustWithAutoLightParams({
				adjustments,
				inputBefore,
				pixelWeights,
				baseRenderOptions,
				lightStrength,
				isDark: isRefDark,
			})
		}

		adjustmentsSkinToneOptimized = adjustWithAutoLightParams({
			adjustments: adjustmentsSkinToneOptimized,
			inputBefore,
			pixelWeights,
			baseRenderOptions,
			lightStrength,
			isDark: isRefDark,
		})
	}

	logColorMatch("!!estimated adjustments", {
		initialAdjustments,
		adjustmentsSkinToneOptimized,
	})

	const mergedLut3d = outputLut ? getMergedLut(luts.lut1d, luts.lut3d) : new Float32Array(0)

	return { adjustments, adjustmentsSkinToneOptimized, mergedLut3d, runtimeInfo }
}

function estimateParameters(options: {
	graySrgbBefore: Float32Array
	graySrgbAfter: Float32Array
	srgbBefore: Float32Array
	srgbAfter: Float32Array
	whiteAreaAfter: Float32Array
	pixelWeights: number[]
	faceAfter: Float32Array | null
	asShotWB: AsShotWB
	baseRenderOptions: RenderOptions
	inputFeatures: InputColorMatchFeaturesWithFaceData
	referenceFeatures: ReferenceColorMatchFeatures
	baseAdjustments?: Partial<Adjustments>
	computeWb: boolean
	baseWb: { temperature?: number; tint?: number }
	maxExposure: number
	enableHighlightCorrectionForThisPhoto: boolean
	featureDist: number
}): Partial<Adjustments> {
	const {
		graySrgbBefore,
		graySrgbAfter,
		srgbBefore,
		srgbAfter,
		whiteAreaAfter,
		pixelWeights,
		faceAfter,
		asShotWB,
		baseRenderOptions,
		inputFeatures,
		referenceFeatures,
		baseAdjustments,
		maxExposure,
		enableHighlightCorrectionForThisPhoto,
		computeWb,
		baseWb,
		featureDist,
	} = options
	const refSatStats = referenceFeatures.satLumStats
	const isBw = refSatStats && refSatStats.totalAvgSat < 0.001
	const isCloseToBw = isNearlyBW(refSatStats)

	const prophotoAfter = applyTransform(srgbAfter, srgbToProphoto)
	const gammaAfter = applyTransform(prophotoAfter, linearToGamma)

	const grayProphotoAfter = applyTransform(graySrgbAfter, srgbToProphoto)
	const grayGammaAfter = applyTransform(grayProphotoAfter, linearToGamma)

	const concatedGammaAfter = new Float32Array(grayGammaAfter.length + gammaAfter.length)
	concatedGammaAfter.set(grayGammaAfter)
	concatedGammaAfter.set(gammaAfter, grayGammaAfter.length)

	const concatedSrgbBefore = new Float32Array(graySrgbBefore.length + srgbBefore.length)
	concatedSrgbBefore.set(graySrgbBefore)
	concatedSrgbBefore.set(srgbBefore, graySrgbBefore.length)

	const toneMapInvBefore = applyTransform(srgbBefore, srgbToToneMapInversed)
	const defaultOptimizationOptions = {
		targetImage: gammaAfter,
		sourceImage: toneMapInvBefore,
		inputColorSpace: ColorSpaceCpu.toneMapInversed,
		outputColorSpace: ColorSpaceCpu.gamma,
		weights: pixelWeights,
		baseRenderOptions,
		stepSize: 0.06,
		maxDelta: [-0.5, 0.5] as [number, number],
		errorType: "L1" as const,
	}

	//const computeWB = (baseAdjustments?.temperature == undefined ? true : false)
	let adjustments: Partial<Adjustments> = {}

	// Get white balance
	if (computeWb) {
		let { temperature, tint } = getWhiteBalance(
			whiteAreaAfter,
			asShotWB.asShotTemp,
			asShotWB.asShotTint,
			baseWb,
		)
		logColorMatch("[white balance] initial", {
			originalDTemp: temperature - asShotWB.asShotTemp,
			originalDTint: tint - asShotWB.asShotTint,
		})
		// if base adjustments are provided, we should not deviate too much from the asShotWB because the above WB does not take into account the base adjustments's color influence
		if (baseAdjustments) {
			const dTtemp = getDecreasedValue(
				temperature - asShotWB.asShotTemp,
				asShotWB.asShotTemp * 0.07,
				0.5,
			)
			const dTint = getDecreasedValue(tint - asShotWB.asShotTint, 7, 0.4)
			temperature = asShotWB.asShotTemp + dTtemp
			tint = asShotWB.asShotTint + dTint
			logColorMatch("[white balance] adjusted", {
				dTtemp,
				dTint,
				temperature,
				tint,
			})
		}
		adjustments = {
			temperature,
			tint,
		}
	}

	if (baseAdjustments) {
		adjustments = { ...baseAdjustments, ...adjustments }
	}

	const renderOptions = {
		...baseRenderOptions,
		inputColorSpace: ColorSpaceCpu.toneMapInversed,
		outputColorSpace: ColorSpaceCpu.proPhoto,
	}
	const lightingLimits = getLightingLimits({ baseAdjustments })

	// Get exposure
	let prophotoCurrent = cachedRenderCpu(toneMapInvBefore, adjustments, renderOptions)
	const doTonemapInv = baseAdjustments == undefined
	let exposure = getExposure(prophotoCurrent, prophotoAfter, pixelWeights, doTonemapInv)

	// check NaN or Infinity and send to Sentry
	if (isNaN(exposure) || !isFinite(exposure)) {
		console.error("Exposure is NaN or Infinity", exposure)
		Sentry.captureException(new Error("Exposure is NaN or Infinity"), {
			extra: {
				exposure,
				prophotoCurrent: isAnyNaNorInf(prophotoCurrent),
				prophotoCurrent10: prophotoCurrent.slice(0, 10),
				prophotoAfter: isAnyNaNorInf(prophotoAfter),
				pixelWeights: isAnyNaNorInf(pixelWeights),
				adjustments: JSON.stringify(adjustments),
			},
		})
	}

	// we should already included a proper initial exposure value in baseAdjustments,
	// set it to 0 to try to use highlights/shadows more; but we allow decrease exposure a bit here
	if (baseAdjustments) {
		const maxExposureDelta = Math.max(0, maxExposure - (baseAdjustments.exposure ?? 0) - 0.3)
		exposure = Math.min(exposure * 0.7, maxExposureDelta)
	}
	let minExposure = -2
	if (inputFeatures.avgFaceHsvl) {
		const minLumTarget = inputFeatures.avgFaceHsvl[1] > 0.55 ? 0.25 : 0.4
		minExposure = Math.min(maxExposure, (minLumTarget - inputFeatures.avgFaceHsvl[3]) * 3)
	}
	adjustments.exposure = clamp((adjustments.exposure ?? 0) + exposure, minExposure, maxExposure)
	logColorMatch("[exposure] initial exposure", {
		delta: exposure,
		result: adjustments.exposure,
		minExposure,
		maxExposure,
	})

	// Get shadows/highlights
	const grayProphotoCurrent = renderCpu(graySrgbBefore, adjustments, baseRenderOptions)
	adjustments = getHighlightsShadows({
		prophotoCurrent: grayProphotoCurrent,
		gammaAfter: grayGammaAfter,
		srgbBefore: graySrgbBefore,
		adjustments,
		baseRenderOptions,
		lightingLimits,
	})

	// Get initial saturation
	if (isBw) {
		adjustments.colorProfile = ColorProfileName.StandardMonochrome
	} else {
		prophotoCurrent = cachedRenderCpu(toneMapInvBefore, adjustments, renderOptions)
		const maxLimit = baseAdjustments ? (baseAdjustments.saturation ?? 0) + 0.1 : 0.1
		const minLimit = baseAdjustments
			? (baseAdjustments.saturation ?? 0) - 0.1
			: isCloseToBw
				? -1
				: -0.3
		adjustments = getSaturation({
			prophotoCurrent,
			prophotoAfter,
			toneMapInvBefore,
			adjustments,
			baseRenderOptions,
			pixelWeights,
			iterations: 2,
			maxLimit,
			minLimit,
		})
	}

	// early return if base adjustments are provided (only compute auto on top of base adjustments)
	if (baseAdjustments) {
		// light params again using iterations
		adjustments = iterateLightParams({
			adjustments,
			maxExposure,
			enableHighlightCorrectionForThisPhoto,
			srgbAfter,
			toneMapInvBefore,
			pixelWeights,
			defaultOptimizationOptions,
			lightingLimits,
		})

		// temperature and tint again using iterations
		if (computeWb) {
			adjustments = iterateWhiteBalance({
				adjustments,
				asShotWB,
				referenceFeatures,
				inputFeatures,
				faceAfter,
				featureDist,
				defaultOptimizationOptions,
			})
		}

		// Adjust saturation again
		if (!isBw) {
			const baseSaturation = baseAdjustments?.saturation ?? 0
			const vibrance = baseAdjustments?.vibrance ?? 0
			prophotoCurrent = cachedRenderCpu(toneMapInvBefore, adjustments, renderOptions)
			adjustments = getSaturation({
				prophotoCurrent,
				prophotoAfter,
				toneMapInvBefore,
				adjustments,
				baseRenderOptions,
				pixelWeights,
				iterations: 4,
				maxLimit: Math.max(0, baseSaturation + 0.2),
				minLimit: Math.min(0, baseSaturation - 0.2),
			})
			const baseSat = baseSaturation * 0.4 + -vibrance * 0.7 * 0.6
			const initialSaturation = adjustments.saturation ?? 0
			const deltaSat = getDecreasedValue(initialSaturation - baseSat, 0.04, 0.5)
			adjustments.saturation = baseSat + deltaSat
			logColorMatch("[saturation] deltaSat", {
				deltaSat,
				baseSat,
				initialSaturation,
			})
		}

		adjustments = limitHighlightsShadows({ adjustments, baseAdjustments })

		if (computeWb) {
			adjustments = correctWhiteColor({
				toneMapInvBefore,
				pixelWeights,
				adjustments,
				referenceFeatures,
				asShotWB,
				baseRenderOptions,
				correctBlueHue: false,
				isCloseToBw,
			})
		}

		return adjustments
	}

	// Continue with no base adjustments case...

	// Initial contrast
	adjustments = findOptimalParameter({
		...defaultOptimizationOptions,
		initialAdjustments: adjustments,
		parameter: "contrast",
		errorType: "Gray",
	})

	// Get curves and apply inverse curves to the target image, as curves are applied last in renderer-cpu
	const { curves, curveStrengthReduced } = getCurves({
		concatedGammaAfter,
		concatedSrgbBefore,
		adjustments,
		baseRenderOptions,
		pixelWeights,
		isBw,
	})
	const prophotoAfterNoCurves = renderInverseCurveProPhoto(prophotoAfter, curves)
	const srgbAfterNoCurves = applyTransform(prophotoAfterNoCurves, prophotoToSrgb)
	const gammaAfterNoCurves = applyTransform(prophotoAfterNoCurves, linearToGamma)
	defaultOptimizationOptions.targetImage = gammaAfterNoCurves

	// if curve strength is decreased, we need to iterate light params again
	if (curveStrengthReduced) {
		adjustments = iterateLightParams({
			adjustments,
			maxExposure,
			enableHighlightCorrectionForThisPhoto,
			srgbAfter: srgbAfterNoCurves,
			toneMapInvBefore,
			pixelWeights,
			defaultOptimizationOptions,
			lightingLimits,
		})
	}

	// Get saturation again
	if (!isBw) {
		prophotoCurrent = cachedRenderCpu(toneMapInvBefore, adjustments, renderOptions)
		adjustments = getSaturation({
			prophotoCurrent,
			prophotoAfter: prophotoAfterNoCurves,
			toneMapInvBefore,
			adjustments,
			baseRenderOptions,
			pixelWeights,
			iterations: 3,
			maxLimit: 0.3,
		})
		adjustments.saturation = getDecreasedValue(adjustments.saturation ?? 0, 0.2, 0.5)
	}

	// Find temporary white balance through white color correction using max strength 0.4
	// This makes the following parameters estimation to be based on a wb that's closer to the final wb

	// Add curves to adjustments
	const validCurves = Object.fromEntries(
		Object.entries(curves).filter(([_, value]) => value != undefined),
	)
	adjustments = { ...adjustments, ...validCurves }
	logColorMatch("Added curves", validCurves)

	adjustments = correctWhiteColor({
		toneMapInvBefore,
		pixelWeights,
		adjustments,
		referenceFeatures,
		asShotWB,
		baseRenderOptions,
		isCloseToBw,
		correctSaturation: false,
		correctBlueHue: false,
		maxStrength: 0.4,
	})

	// remove curves from adjustments
	for (const key in validCurves) {
		delete adjustments[key]
	}

	// Get calibration
	prophotoCurrent = cachedRenderCpu(toneMapInvBefore, adjustments, renderOptions)
	adjustments = getCalibration({
		prophotoCurrent,
		prophotoAfter: prophotoAfterNoCurves,
		toneMapInvBefore,
		pixelWeights,
		adjustments,
		baseRenderOptions,
	})

	// Get HSL
	prophotoCurrent = cachedRenderCpu(toneMapInvBefore, adjustments, renderOptions)
	adjustments = getHSL({
		prophotoCurrent,
		prophotoAfter: prophotoAfterNoCurves,
		pixelWeights,
		adjustments,
		baseRenderOptions,
		isCloseToBw,
		averageHSL: referenceFeatures.averageAdjustments,
	})
	logColorMatch("[getHSL] Result of hsl", structuredClone(adjustments))

	// Get dehaze
	adjustments = findOptimalParameter({
		...defaultOptimizationOptions,
		initialAdjustments: adjustments,
		parameter: "dehaze",
		stepSize: 0.04,
	})

	if (isBw) {
		if (curves.curvesAll != undefined) {
			adjustments.curvesAll = curves.curvesAll
		}
		return adjustments
	}

	// Get point colors
	// We disable it because it tends to make skin tone not natural or cause other artifacts
	if (enablePointColors) {
		adjustments = getPointColors({
			toneMapInvBefore,
			target: prophotoAfterNoCurves,
			adjustments,
			baseRenderOptions,
			pixelWeights,
		})
	}

	// Get color grading
	adjustments = getColorGrading({
		toneMapInvBefore,
		target: gammaAfterNoCurves,
		adjustments,
		baseRenderOptions,
		pixelWeights,
		refGrayColorMean: referenceFeatures.grayColor ?? null,
	})

	// Add curves to adjustments
	adjustments = { ...adjustments, ...validCurves }

	defaultOptimizationOptions.targetImage = gammaAfter

	// Iterate white balance after curves are applied
	adjustments = iterateWhiteBalance({
		adjustments,
		asShotWB,
		referenceFeatures,
		inputFeatures,
		faceAfter,
		featureDist,
		defaultOptimizationOptions,
	})

	// Get vibrance after curves are applied
	if (!isBw) {
		adjustments = findOptimalParameter({
			...defaultOptimizationOptions,
			initialAdjustments: adjustments,
			parameter: "vibrance",
			stepSize: 0.04,
			errorType: "Tone",
			maxDelta: [-0.2, 0.2],
		})
		adjustments.vibrance = getDecreasedValue(adjustments.vibrance ?? 0, 0.08, 0.5)

		// if vibrance is increased significantly, we need to iterate white balance again
		if (adjustments.vibrance > 0.06) {
			adjustments = iterateWhiteBalance({
				adjustments,
				asShotWB,
				referenceFeatures,
				inputFeatures,
				faceAfter,
				featureDist,
				defaultOptimizationOptions,
			})
		}
	}

	// Adjust contrast again
	adjustments = findOptimalParameter({
		targetImage: srgbAfter,
		sourceImage: toneMapInvBefore,
		weights: pixelWeights,
		initialAdjustments: adjustments,
		baseRenderOptions,
		parameter: "contrast",
		stepSize: 0.04,
		outputColorSpace: ColorSpaceCpu.sRGB,
	})

	// Additional changes below that doesn't target LUTed output anymore

	// Adjust HSL saturation
	if (refSatStats && !referenceFeatures.averageAdjustments) {
		// Adjust HSL saturation for bw or nearly bw reference images
		adjustments = adjustForLowSatRef({
			adjustments,
			refSatStats,
			pixelWeights,
			toneMapInvBefore,
			baseRenderOptions,
		})
		// Reduce high HSL saturation if reference is less saturated for specific hues
		adjustments = reduceHighHSLSaturation({
			adjustments,
			refSatStats,
			pixelWeights,
			toneMapInvBefore,
			baseRenderOptions,
		})
	}

	adjustments = limitHighlightsShadows({ adjustments })

	// Increase contrast to a minimum target because the model tends to make contrast low
	const currContrast = adjustments.contrast ?? 0
	const targetContrast = Math.min(
		Math.max(currContrast + 0.1, (adjustments.shadows ?? 0) * 0.4),
		0.3,
	)
	if (currContrast < targetContrast) {
		const delta = targetContrast - currContrast
		adjustments.contrast = currContrast + Math.min(delta, 0.25)
	}

	// Check curvesAll: it tends to over-expose if it's lifted up in the middle region (120 - 200)
	// Bring it down if it's too high and increase contrast to compensate for the contrast loss
	adjustments = softenCurveUpperMid(adjustments)

	// 2nd time: try to make saturation value not too large for natural look
	const currSaturation = adjustments.saturation ?? 0
	if ((!isCloseToBw && currSaturation < -0.05) || currSaturation > 0.05) {
		adjustments.saturation = getDecreasedValue(currSaturation, 0.05, 0.4)
	}

	// Make sure orange and red are not too desaturated
	if (!isCloseToBw) {
		adjustments = maintainOrangeRedSaturation(adjustments)
	}

	// Correct white color using white balance according to ref's white region
	adjustments = correctWhiteColor({
		toneMapInvBefore,
		pixelWeights,
		adjustments,
		referenceFeatures,
		asShotWB,
		baseRenderOptions,
		isCloseToBw,
	})

	// Correct vegetation using green and yellow hues when there is no base adjustments
	adjustments = correctVegetation({
		adjustments,
		srgbBefore,
		pixelWeights,
		asShotWB,
		baseRenderOptions,
		referenceFeatures,
		hasBaseAdjustments: false,
		averageHSL: referenceFeatures.averageAdjustments,
	})

	return adjustments
}

function getMaxExposure(options: {
	baseAdjustments: Partial<Adjustments> | undefined
	maxExposure?: number
}): number {
	const { baseAdjustments } = options
	let maxExposure = (options.maxExposure ?? 4.2) + 0.8
	if (baseAdjustments) {
		maxExposure = options.maxExposure ?? 5
		const baseExposure = baseAdjustments.exposure ?? 0
		if (maxExposure < baseExposure) {
			maxExposure = baseExposure * 0.7 + maxExposure * 0.3
		}
		logColorMatch("!!maxExposure", {
			maxExposure,
			baseExposure,
			initialMaxExposure: options.maxExposure,
		})
	}
	const result = Math.min(
		Math.max(maxExposure, 0.75),
		Math.max((baseAdjustments?.exposure ?? 4) + 0.15, maxExposure),
	)
	if (isNaN(result)) {
		return 5.0
	}
	return result
}

function iterateLightParams(options: {
	adjustments: Partial<Adjustments>
	maxExposure: number
	srgbAfter: Float32Array
	toneMapInvBefore: Float32Array
	pixelWeights: number[]
	enableHighlightCorrectionForThisPhoto: boolean
	lightingLimits: LightingLimits
	defaultOptimizationOptions: {
		targetImage: Float32Array
		sourceImage: Float32Array
		inputColorSpace: ColorSpaceCpu
		outputColorSpace: ColorSpaceCpu
		weights: number[]
		baseRenderOptions: RenderOptions
		stepSize: number
		maxDelta: vec2
		errorType?: "L1" | "Gray" | "Tone"
	}
}): Partial<Adjustments> {
	const {
		maxExposure,
		enableHighlightCorrectionForThisPhoto,
		srgbAfter,
		toneMapInvBefore,
		pixelWeights,
		defaultOptimizationOptions,
		lightingLimits,
	} = options
	let adjustments = { ...options.adjustments }
	const baseRenderOptions = defaultOptimizationOptions.baseRenderOptions

	// exposure again using iterations
	adjustments = findOptimalParameter({
		...defaultOptimizationOptions,
		initialAdjustments: adjustments,
		parameter: "exposure",
		maxDelta: [-0.5, maxExposure - (adjustments.exposure ?? 0)],
	})

	// shadows again using iterations
	adjustments = findOptimalParameter({
		...defaultOptimizationOptions,
		initialAdjustments: adjustments,
		parameter: "shadows",
		rangeLimits: [lightingLimits.minShadows, lightingLimits.maxShadows],
	})

	// whites again using iterations
	adjustments = findOptimalParameter({
		...defaultOptimizationOptions,
		initialAdjustments: adjustments,
		parameter: "whites",
		rangeLimits: [lightingLimits.minWhites, lightingLimits.maxWhites],
	})

	// highlights again using iterations
	adjustments = findOptimalParameter({
		...defaultOptimizationOptions,
		initialAdjustments: adjustments,
		parameter: "highlights",
		rangeLimits: [lightingLimits.minHighlights, lightingLimits.maxHighlights],
	})

	// contrast
	adjustments = findOptimalParameter({
		targetImage: srgbAfter,
		sourceImage: toneMapInvBefore,
		weights: pixelWeights,
		initialAdjustments: adjustments,
		baseRenderOptions,
		parameter: "contrast",
		stepSize: 0.05,
		outputColorSpace: ColorSpaceCpu.sRGB,
		rangeLimits: [lightingLimits.minContrast, lightingLimits.maxContrast],
	})

	// blacks
	adjustments = findOptimalParameter({
		targetImage: srgbAfter,
		sourceImage: toneMapInvBefore,
		weights: pixelWeights,
		initialAdjustments: adjustments,
		baseRenderOptions,
		parameter: "blacks",
		rangeLimits: [lightingLimits.minBlacks, lightingLimits.maxBlacks],
		stepSize: 0.05,
		outputColorSpace: ColorSpaceCpu.sRGB,
	})

	// whites / highlights correction to avoid over-exposure
	if (enableHighlightCorrectionForThisPhoto) {
		adjustments = applyHighlightsCorrection({
			adjustments,
			baseRenderOptions: defaultOptimizationOptions.baseRenderOptions,
			pixelWeights,
			toneMapInvBefore,
			maxExposure,
		})
	}

	return adjustments
}

// sometimes color match makes image too contrasty, so we need to limit highlights to be not too + and shadows not too -
function limitHighlightsShadows(options: {
	adjustments: Partial<Adjustments>
	baseAdjustments?: Partial<Adjustments>
}) {
	const { adjustments, baseAdjustments } = options
	let highlights = adjustments.highlights ?? 0
	let shadows = adjustments.shadows ?? 0
	let whites = adjustments.whites ?? 0

	if (baseAdjustments) {
		const baseHighlights = baseAdjustments.highlights ?? 0
		const baseShadows = baseAdjustments.shadows ?? 0
		const baseWhites = baseAdjustments.whites ?? 0

		const minShadows = Math.min(0.1, baseShadows - 0.1)
		if (shadows < minShadows) {
			shadows = minShadows + (shadows - minShadows) * 0.3
		}

		const maxHighlights = Math.max(-0.1, baseHighlights + 0.1)
		if (highlights > maxHighlights) {
			highlights = maxHighlights + (highlights - maxHighlights) * 0.3
		}

		const maxWhites = Math.max(0, baseWhites + 0.1)
		if (whites > maxWhites) {
			whites = maxWhites + (whites - maxWhites) * 0.3
		}
	} else {
		if (shadows < 0) {
			shadows *= 0.5
		}
		if (highlights > 0) {
			highlights *= 0.5
		}
		if (whites > 0) {
			whites *= 0.5
		}
	}

	adjustments.highlights = highlights
	adjustments.shadows = shadows
	adjustments.whites = whites

	return adjustments
}

function iterateWhiteBalance(options: {
	adjustments: Partial<Adjustments>
	asShotWB: { asShotTemp: number; asShotTint: number }
	referenceFeatures: ReferenceColorMatchFeatures
	inputFeatures: InputColorMatchFeaturesWithFaceData
	faceAfter: Float32Array | null
	featureDist: number
	defaultOptimizationOptions: {
		targetImage: Float32Array
		sourceImage: Float32Array
		inputColorSpace: ColorSpaceCpu
		outputColorSpace: ColorSpaceCpu
		weights: number[]
		baseRenderOptions: RenderOptions
		stepSize: number
		maxDelta: vec2
		errorType?: "L1" | "Gray" | "Tone"
	}
}): Partial<Adjustments> {
	const {
		defaultOptimizationOptions,
		asShotWB,
		referenceFeatures,
		inputFeatures,
		faceAfter,
		featureDist,
	} = options
	let adjustments = { ...options.adjustments }

	const refwb = referenceFeatures.whiteBalance
	const refTempDeltaOriginal =
		((refwb?.temperature ?? 0) - (refwb?.asShotTemperature ?? 0)) /
		(refwb?.asShotTemperature ?? 1)
	const refTintDeltaOriginal = (refwb?.tint ?? 0) - (refwb?.asShotTint ?? 0)

	const { tempBiasFinal: refTempDelta, tintBiasFinal: refTintDelta } = getClampedWBBias({
		featureDist,
		tempBias: refTempDeltaOriginal,
		tintBias: refTintDeltaOriginal,
	})
	const minTemp = Math.min(
		asShotWB.asShotTemp * (0.92 + refTempDelta),
		(adjustments.temperature ?? 20000) * 0.95,
	)
	const maxTemp = Math.max(
		asShotWB.asShotTemp * (1.08 + refTempDelta),
		(adjustments.temperature ?? 0) * 1.05,
	)
	const minDeltaTemp = Math.max(
		minTemp - (adjustments.temperature ?? asShotWB.asShotTemp),
		asShotWB.asShotTemp * -0.1,
	)
	const maxDeltaTemp = Math.min(
		maxTemp - (adjustments.temperature ?? asShotWB.asShotTemp),
		asShotWB.asShotTemp * 0.15,
	)
	adjustments = findOptimalParameter({
		...defaultOptimizationOptions,
		initialAdjustments: adjustments,
		parameter: "temperature",
		stepSize: Math.max(80, asShotWB.asShotTemp * 0.02),
		errorType: "Tone",
		maxDelta: [minDeltaTemp, maxDeltaTemp],
		errorMargin: 0.00015,
		originalValue: asShotWB.asShotTemp,
	})

	const extra = 8

	const minTint = Math.min(
		asShotWB.asShotTint + Math.min(Math.max(refTintDelta, 0) - 10, refTintDelta),
		asShotWB.asShotTint - 2,
		(adjustments.tint ?? 0) - extra,
	)
	const maxTint = Math.max(
		asShotWB.asShotTint + Math.max(Math.min(refTintDelta, 0) + 10, refTintDelta),
		asShotWB.asShotTint + 2,
		(adjustments.tint ?? 0) + extra,
	)
	const minDeltaTint = Math.max(minTint - (adjustments.tint ?? asShotWB.asShotTint), -10 - extra)
	const maxDeltaTint = Math.min(maxTint - (adjustments.tint ?? asShotWB.asShotTint), 10 + extra)
	const faceWeight = step(inputFeatures.facePercent, 0.0, 0.01) * 0.4
	logColorMatch("[tint]", {
		asShotWB,
		refTintDelta,
		minTint,
		maxTint,
		minDeltaTint,
		maxDeltaTint,
		faceWeight,
	})
	adjustments = findOptimalParameter({
		...defaultOptimizationOptions,
		initialAdjustments: adjustments,
		parameter: "tint",
		stepSize: 3,
		maxDelta: [minDeltaTint, maxDeltaTint],
		errorType: "Tone",
		errorMargin: 0.00015,
		originalValue: asShotWB.asShotTint,
		faceBefore: inputFeatures.faceColors,
		faceAfter,
		faceWeights: inputFeatures.faceWeights,
		faceWeight: faceWeight,
	})

	logColorMatch("[wb iterations] result", adjustments.temperature, adjustments.tint)

	return adjustments
}

function addParameter(
	adjustments: Partial<Adjustments>,
	parameter: string,
	value: number,
): Partial<Adjustments> {
	const newAdjustments = { ...adjustments }
	newAdjustments[parameter] = clamp(
		(newAdjustments[parameter] ?? 0) + value,
		...getAdjustmentRange(parameter as keyof Adjustments),
	)

	// dehaze makes image darker, so we need to adjust exposure to compensate
	if (parameter == "dehaze") {
		newAdjustments.exposure = (newAdjustments.exposure ?? 0) + value * 0.5
	}

	return newAdjustments
}

function findOptimalParameter({
	targetImage, // prophotoGamma, should be consistent with outputColorSpace
	sourceImage, // toneMapInvBefore
	weights,
	initialAdjustments,
	baseRenderOptions,
	parameter,
	stepSize = 0.05,
	errorType = "L1",
	outputColorSpace = ColorSpaceCpu.gamma,
	inputColorSpace = ColorSpaceCpu.toneMapInversed,
	maxDelta = [-0.8, 0.8],
	errorMargin = 0.0,
	originalValue = 0,
	faceBefore,
	faceAfter,
	faceWeights,
	faceWeight = 0.5,
	rangeLimits,
}: {
	targetImage: Float32Array
	sourceImage: Float32Array
	weights: number[]
	initialAdjustments: Partial<Adjustments>
	baseRenderOptions: RenderOptions
	parameter: keyof Adjustments
	stepSize?: number
	errorType?: "L1" | "Gray" | "Tone"
	outputColorSpace?: ColorSpaceCpu
	inputColorSpace?: ColorSpaceCpu
	maxDelta?: vec2
	errorMargin?: number
	originalValue?: number
	faceBefore?: Float32Array | null
	faceAfter?: Float32Array | null
	faceWeights?: Float32Array | null
	faceWeight?: number
	rangeLimits?: vec2
}): Partial<Adjustments> {
	const renderOptions = {
		...baseRenderOptions,
		outColorSpace: outputColorSpace,
		inputColorSpace: inputColorSpace,
	}
	const faceRenderOptions = {
		...baseRenderOptions,
		outColorSpace: ColorSpaceCpu.sRGB,
		inputColorSpace: ColorSpaceCpu.sRGB,
	}

	// Try initial values to determine direction
	const testValues = [0, -stepSize, stepSize]
	const errors = testValues.map((value) => {
		const adjustments = addParameter(initialAdjustments, parameter, value)
		const rendered = cachedRenderCpu(sourceImage, adjustments, renderOptions)
		let error = 0
		if (errorType == "Gray") {
			error = getWeightedGrayDiff(rendered, targetImage, weights)
		} else if (errorType == "Tone") {
			error = getWeightedToneDiff(rendered, targetImage, weights)
		} else {
			error = getWeightedDiff(rendered, targetImage, weights)
		}
		if (faceBefore && faceAfter && faceWeights && faceBefore.length > 0) {
			const faceRendered = renderCpu(faceBefore, adjustments, faceRenderOptions)
			error = error + getWeightedToneDiff(faceRendered, faceAfter, faceWeights) * faceWeight
		}
		return error
	})
	logColorMatch(`[${parameter}] initial errors`, { errors, testValues, maxDelta })

	// Find best initial value and determine direction
	const bestError = Math.min(...errors)
	let bestValue = testValues[errors.indexOf(bestError)]

	logColorMatch(`[${parameter}] initial value`, bestValue, ", initial error", bestError)

	// If 0 is best, return early
	if (bestValue === 0 || bestValue === undefined) {
		return initialAdjustments
	}

	// Determine search direction based on best initial value
	const direction = bestValue > 0 ? 1 : -1
	const step = stepSize * direction
	let errorReduced = (errors[0] ?? 0) - bestError

	// Iteratively search for better value
	let iterations = 0
	let lastError = bestError
	let value = bestValue

	const initialValue = initialAdjustments[parameter] as number
	while (iterations < 10) {
		// make step larger if error decreasing fast
		if (
			(iterations == 0 && errorReduced > 0.0008) ||
			(iterations > 0 && iterations < 3 && errorReduced > 0.001)
		) {
			value += 2 * step
		} else {
			value += step
		}

		if (value > maxDelta[1] || value < maxDelta[0]) {
			break
		}
		if (
			rangeLimits &&
			(value + initialValue > rangeLimits[1] || value + initialValue < rangeLimits[0])
		) {
			break
		}

		const adjustments = addParameter(initialAdjustments, parameter, value)
		const rendered = cachedRenderCpu(sourceImage, adjustments, renderOptions)
		let error =
			errorType == "Gray"
				? getWeightedGrayDiff(rendered, targetImage, weights)
				: errorType == "Tone"
					? getWeightedToneDiff(rendered, targetImage, weights)
					: getWeightedDiff(rendered, targetImage, weights)
		if (faceBefore && faceAfter && faceWeights && faceBefore.length > 0) {
			const faceRendered = renderCpu(faceBefore, adjustments, faceRenderOptions)
			const faceError = getWeightedToneDiff(faceRendered, faceAfter, faceWeights)
			logColorMatch(`[${parameter}] face error`, faceError, ", image error", error)
			error = error + faceError * faceWeight
		}
		logColorMatch(
			`[${parameter}] iterations`,
			iterations,
			`, ${parameter}`,
			value,
			", error",
			error,
		)

		// If error starts increasing, we've gone too far
		if (error > lastError) {
			break
		}

		// If error is not decreasing fast, and value is deviating from original value, stop
		if (
			error > lastError - errorMargin &&
			Math.abs(initialValue + value - originalValue) >
				Math.abs(initialValue + bestValue - originalValue)
		) {
			break
		}

		bestValue = value

		// If improvement is minimal, reduce step size
		// if (lastError - error < 0.0001) {
		// 	step = 0.5 * stepSize * direction
		// }

		errorReduced = lastError - error
		lastError = error
		iterations++
	}

	return addParameter(initialAdjustments, parameter, bestValue)
}

export function getExposure(
	before: Float32Array,
	after: Float32Array,
	pixelWeights: number[],
	doTonemapInv: boolean = true,
): number {
	const beforeInv = doTonemapInv ? applyTransform(before, tonemapInv) : before
	const afterInv = doTonemapInv ? applyTransform(after, tonemapInv) : after

	if (pixelWeights.length > 0) {
		// Calculate weighted means using the weights
		let weightedMeanBefore = 0
		let weightedMeanAfter = 0
		const pixelCount = before.length / 3

		for (let i = 0; i < pixelCount; i++) {
			const weight = pixelWeights[i] ?? 0
			// Average RGB values for each pixel
			const meanBefore =
				((beforeInv[i * 3] ?? 0) +
					(beforeInv[i * 3 + 1] ?? 0) +
					(beforeInv[i * 3 + 2] ?? 0)) /
				3
			const meanAfter =
				((afterInv[i * 3] ?? 0) + (afterInv[i * 3 + 1] ?? 0) + (afterInv[i * 3 + 2] ?? 0)) /
				3

			weightedMeanBefore += Math.max(meanBefore, 0.0) * weight
			weightedMeanAfter += Math.max(meanAfter, 0.0) * weight
		}

		logColorMatch(
			"[exposure] weightedMeanBefore",
			weightedMeanBefore,
			", weightedMeanAfter",
			weightedMeanAfter,
		)

		return clamp(
			Math.log2(
				Math.max(weightedMeanAfter, 0.000001) / Math.max(weightedMeanBefore, 0.000001),
			),
			-5,
			5,
		)
	} else {
		// Fall back to simple mean if no weights provided
		return clamp(
			Math.log2(
				Math.max(getMean(afterInv), 0.000001) / Math.max(getMean(beforeInv), 0.000001),
			),
			-5,
			5,
		)
	}
}

export function getWhiteBalance(
	after: Float32Array,
	initialTemp: number = 5000,
	initialTint: number = 0,
	baseWB: { temperature?: number; tint?: number },
): { temperature: number; tint: number } {
	// Calculate white point from the top portion of the gray band
	// const startIndex = 200 * 3 .slice(startIndex, endIndex)
	// const endIndex = 256 * 3
	const rs = after.filter((_, i) => i % 3 === 0)
	const gs = after.filter((_, i) => i % 3 === 1)
	const bs = after.filter((_, i) => i % 3 === 2)
	const whitePointR = getMean(rs)
	const whitePointG = getMean(gs)
	const whitePointB = getMean(bs)
	const afterWhitePoint: vec3 = [whitePointR, whitePointG, whitePointB]

	logColorMatch("[white balance] after_white_point", afterWhitePoint)

	const result = getWhiteBlanceParams({
		afterWhitePointSrgb: afterWhitePoint,
		initialTemp: baseWB.temperature ?? initialTemp,
		initialTint: baseWB.tint ?? initialTint,
		whitePointXyz: illuminants.D65,
	})
	logColorMatch("[white balance] result", { baseWB, initialTemp, initialTint, result })

	return result
}

function getWhiteBlanceParams(options: {
	afterWhitePointSrgb: vec3
	initialTemp: number
	initialTint: number
	whitePointXyz: vec3
}) {
	const { afterWhitePointSrgb, initialTemp, initialTint, whitePointXyz } = options

	// Convert to different color spaces
	const xyzAfter = srgb_to_xyz(afterWhitePointSrgb) // Note this RGB_TO_XYZ matrix is not the same as the one used in the renderer, but seems doesn't matter
	const lmsAfter = xyz_to_lms(xyzAfter)
	const lmsD65 = xyz_to_lms(whitePointXyz)

	// If use the same rgbToXyz matrix as in renderer, then we need to use actual white point here
	// const whitePointXyz = srgb_to_xyz([1, 1, 1])
	// const lmsD65 = xyz_to_lms(whitePointXyz)

	// Calculate as-shot white point
	const xyzAsShot = XYtoXYZ(whiteBalanceToXY([initialTemp, initialTint]))
	const lmsAsShot = xyz_to_lms(xyzAsShot)

	// asshot / adjusted = after / D65
	const lmsAdjusted: vec3 = [
		(lmsAsShot[0] * lmsD65[0]) / lmsAfter[0],
		(lmsAsShot[1] * lmsD65[1]) / lmsAfter[1],
		(lmsAsShot[2] * lmsD65[2]) / lmsAfter[2],
	]

	const xyz = lms_to_xyz(lmsAdjusted)
	const [temperature, tint] = XYtoWhiteBalance(XYZtoXY(xyz))
	logColorMatch("[white balance] temperature", temperature, ", tint", tint)

	if (!Number.isFinite(temperature) || !Number.isFinite(tint)) {
		// Diagnostic-only: a degenerate whitePointXyz or afterWhitePointSrgb (all-zero, NaN,
		// or Inf) — usually the LUT-output white-region mean — divides by zero
		// in lmsAdjusted, producing NaN temp/tint. We let it propagate so the
		// downstream `image-processing.ts` NaN check switches to old auto /
		// surfaces an error to the user, instead of masking the bug with a
		// plausible initial-WB substitute.
		Sentry.captureMessage("Color match getWhiteBlanceParams produced non-finite temp/tint", {
			level: "warning",
			extra: {
				temperature,
				tint,
				afterWhitePointSrgb,
				initialTemp,
				initialTint,
				whitePointXyz,
				lmsAdjusted,
			},
		})
	}

	return {
		temperature: clamp(temperature, 2100, Math.min(20000, initialTemp + 6000)),
		tint: clamp(tint, Math.max(initialTint - 70, -100), Math.min(initialTint + 70, 100)),
	}
}

/**
 * Corrects the neural network's systematic warm bias by aligning white/gray
 * regions with the reference. High-level flow:
 *
 *   1. Find white regions in the current output; fall back to gray if none found
 *   2. Compute correction strength (reduced when few white pixels or target looks saturated)
 *   3. Iterative WB loop (up to 3 rounds): measure white-point drift → adjust temp/tint → re-render → check convergence
 *   4. Scale final delta by strength; extra limits for gray-color path
 *   5. Compensate side-effects: blue/purple hue shift from lowered temp, and desaturation on faces
 *
 * This is a heuristic — it works well broadly but not necessarily optimal.
 * Improvements are welcome when there's a clear pattern, but changes affect many photos.
 * Regression-test on: style-from-jpegs-test/regression*, Dropbox-ImageCompare-InputTestData/AaronData1-Jamela
 */
function correctWhiteColor(options: {
	toneMapInvBefore: Float32Array
	pixelWeights: number[]
	adjustments: Partial<Adjustments>
	asShotWB: { asShotTemp: number; asShotTint: number }
	baseRenderOptions: RenderOptions
	correctBlueHue?: boolean
	correctSaturation?: boolean
	isCloseToBw: boolean
	maxStrength?: number
	referenceFeatures: ReferenceColorMatchFeatures
}) {
	if (!enableWhiteColorCorrection) {
		return options.adjustments
	}
	let whiteColorTarget = options.referenceFeatures.whiteColor ?? null
	const grayColorTarget = options.referenceFeatures.grayColor?.global ?? null
	if (!whiteColorTarget && !grayColorTarget) {
		return options.adjustments
	}

	const {
		toneMapInvBefore,
		adjustments,
		asShotWB,
		baseRenderOptions,
		pixelWeights,
		correctBlueHue = true,
		correctSaturation = true,
		isCloseToBw,
		maxStrength = 1.0,
		referenceFeatures,
	} = options

	const refWB = referenceFeatures.whiteBalance
	logColorMatch("[correctWhiteColor] ref features", { refWB, whiteColorTarget, grayColorTarget })
	logColorMatch("[correctWhiteColor] initialWB", {
		temperature: adjustments.temperature ?? 0,
		tint: adjustments.tint ?? 0,
	})

	const { minTemp, maxTemp, minTint, maxTint } = getWbRange({
		adjustments,
		asShotWB,
		refWB,
	})
	logColorMatch("[correctWhiteColor] wbRange", {
		minTemp,
		maxTemp,
		minTint,
		maxTint,
	})

	const renderOptions = {
		...baseRenderOptions,
		inputColorSpace: ColorSpaceCpu.toneMapInversed,
		outColorSpace: ColorSpaceCpu.sRGB,
	}
	const srgbAfter = cachedRenderCpu(toneMapInvBefore, adjustments, renderOptions)

	// --- Step 1: Find white/gray regions in current output ---
	// Relaxed thresholds vs reference because the model tends to produce warm output
	let whiteColorsAfter = getWhiteColors({
		colors: srgbAfter,
		pixelWeights,
		lumThreshold: 0.65,
		satThreshold: 0.2,
	})
	let useWhiteColor = true
	if (!whiteColorsAfter || !whiteColorTarget) {
		logColorMatch("[correctWhiteColor] no white colors found in input srgbAfter or reference.")
		if (!enableGrayColorCorrection) {
			return adjustments
		}
		whiteColorTarget = grayColorTarget
		whiteColorsAfter = getGrayColors({
			colors: srgbAfter,
			pixelWeights,
		})
		useWhiteColor = false
	}
	if (!whiteColorTarget || !whiteColorsAfter) {
		logColorMatch("[correctWhiteColor] no gray colors found in input srgbAfter or reference.")
		return adjustments
	}

	// --- Step 2: Compute correction strength ---
	// Reduced when: few white/gray pixels, or target itself looks saturated (likely false detection)
	const totalWeight = whiteColorsAfter.totalWeight
	let strength = 1.0
	if (totalWeight < 0.1) {
		strength = (totalWeight / 0.1) * 0.8 + 0.2
	}
	logColorMatch("[correctWhiteColor] totalWeight", totalWeight, ", strength", strength)

	const targetSat = rgbToSat(whiteColorTarget ?? grayColorTarget)
	if (targetSat > 0.04) {
		if (useWhiteColor) {
			strength = Math.min(strength, Math.max(1 - (targetSat - 0.04) / 0.06, 0.0))
		} else {
			strength = Math.min(strength, Math.max(1 - (targetSat - 0.08) / 0.17, 0.0))
		}
		logColorMatch(
			"[correctWhiteColor] targetSat",
			targetSat,
			", strength",
			strength,
			", useWhiteColor",
			useWhiteColor,
		)
	}
	if (strength == 0) {
		return adjustments
	}

	strength = Math.min(strength, maxStrength)

	// --- Step 3: Iterative WB correction loop (up to 3 rounds) ---
	// Each round: measure white-point error → compute temp/tint delta → apply with damping → re-render
	// Converges when delta is small, or when the correction direction is wrong (increasing warmth)
	const initialTemp = adjustments.temperature ?? 0
	const initialTint = adjustments.tint ?? 0

	let whiteSrgbAfter: Float32Array = whiteColorsAfter.whiteColors
	const whiteColorsWeights = whiteColorsAfter.whiteWeights

	let whiteColorAfter: vec3 | null = getWeightedMeanRgb(
		whiteSrgbAfter,
		whiteColorsWeights,
		0.0001,
	)

	const whiteColorsBefore = gatherRGBValues(toneMapInvBefore, whiteColorsAfter.indices)

	for (let i = 0; i < 3; i++) {
		if (whiteColorAfter && whiteColorTarget) {
			// Skip the WB step if the photo's "white-region" mean is itself near
			// black (e.g. clipped shadows or a degenerate render): srgb_to_xyz
			// would return [0, 0, 0] and `normalize_xyz` (inside getWhiteBlanceParams)
			// would divide by zero, producing NaN temp/tint.
			if (isAllNearZero(whiteColorAfter)) {
				logColorMatch(
					"[correctWhiteColor] whiteColorAfter is near-black, skipping WB iteration",
					whiteColorAfter,
				)
				break
			}
			const whiteBalance = getWhiteBlanceParams({
				afterWhitePointSrgb: whiteColorTarget,
				initialTemp: asShotWB.asShotTemp,
				initialTint: asShotWB.asShotTint,
				whitePointXyz: srgb_to_xyz(whiteColorAfter),
			})

			let deltaTemp = whiteBalance.temperature - asShotWB.asShotTemp
			let deltaTint = whiteBalance.tint - asShotWB.asShotTint

			// because model tends to make image too warm, we should not increase temperature for gray color (likely false detection)
			if (!useWhiteColor && deltaTemp > 0) {
				deltaTemp *= 0.3
				deltaTint *= 0.3
			}

			logColorMatch("[correctWhiteColor] iteration", i + 1, {
				whiteColorTarget,
				whiteColorAfter,
				prevTemp: adjustments.temperature,
				prevTint: adjustments.tint,
				deltaTemp,
				deltaTint,
			})

			const scale = i === 0 ? 0.7 : 0.9
			adjustments.temperature = clamp(
				(adjustments.temperature ?? asShotWB.asShotTemp) + deltaTemp * scale,
				minTemp,
				maxTemp,
			)
			adjustments.tint = clamp(
				(adjustments.tint ?? asShotWB.asShotTint) + deltaTint * scale,
				minTint,
				maxTint,
			)

			whiteSrgbAfter = cachedRenderCpu(whiteColorsBefore, adjustments, renderOptions)
			whiteColorAfter = getWeightedMeanRgb(whiteSrgbAfter, whiteColorsWeights, 0.0001)

			if (Math.abs(deltaTemp) < 100 && Math.abs(deltaTint) < 4) {
				break
			}
			// Our neural network tends to make image too warm, normally the correction direction should be lower the temperature
			if (deltaTemp > 150) {
				break
			}
			if (!useWhiteColor && deltaTemp > 0) {
				break
			}

			logColorMatch(
				"[correctWhiteColor] whiteColorAfter sat",
				rgbToSat(whiteColorAfter ?? [0, 0, 0]),
			)
			if (whiteColorAfter && rgbToSat(whiteColorAfter) < 0.04) {
				const deltaTemp = ((adjustments.temperature ?? 0) - initialTemp) * strength
				if (Math.abs(deltaTemp) > 170) {
					// enough temperature change and current sat is low, stop
					break
				}
			}
		} else {
			break
		}
	}

	// --- Step 4: Scale final correction by strength ---
	let deltaTemp = ((adjustments.temperature ?? 0) - initialTemp) * strength
	let deltaTint = ((adjustments.tint ?? 0) - initialTint) * strength
	if (!useWhiteColor) {
		// limit gray color correction
		deltaTemp = clamp(deltaTemp, -asShotWB.asShotTemp * 0.08, asShotWB.asShotTemp * 0.04)
		deltaTint = clamp(deltaTint, -8, 8)
	}
	adjustments.temperature = clamp(initialTemp + deltaTemp, minTemp, maxTemp)
	adjustments.tint = clamp(initialTint + deltaTint, minTint, maxTint)
	logColorMatch("[correctWhiteColor] final wb", {
		temperature: adjustments.temperature,
		tint: adjustments.tint,
		deltaTemp,
		deltaTint,
	})

	// --- Step 5a: Blue/purple hue compensation ---
	// Lowering temperature shifts blues toward cyan and makes them more prominent.
	// Counteract by pushing blue/purple hue warmer and reducing their saturation/luminance.
	deltaTemp = (adjustments.temperature ?? 0) - initialTemp
	if (correctBlueHue && deltaTemp < -100) {
		const currBlueHue = adjustments.hueBlue ?? 0
		let hueDeltaBlue = ((-deltaTemp - 100) / 100) * 0.03
		hueDeltaBlue = clamp(getDecreasedValue(hueDeltaBlue, 0.06, 0.5), 0.0, 0.1)
		adjustments.hueBlue = currBlueHue + hueDeltaBlue
		const minSatBlue = Math.min(-0.4, adjustments.saturationBlue ?? 0)
		adjustments.saturationBlue = Math.max(
			(adjustments.saturationBlue ?? 0) - hueDeltaBlue * 1.5,
			minSatBlue,
		)
		adjustments.luminanceBlue = Math.max(
			(adjustments.luminanceBlue ?? 0) - hueDeltaBlue * 2,
			-0.4,
		)

		const hueDeltaPurple = ((-deltaTemp - 100) / 100) * 0.02
		const minSatPurple = Math.min(-0.4, adjustments.saturationPurple ?? 0)
		adjustments.huePurple = currBlueHue + clamp(hueDeltaPurple, 0, 0.1)
		adjustments.saturationPurple = Math.max(
			(adjustments.saturationPurple ?? 0) - hueDeltaPurple * 1.5,
			minSatPurple,
		)
		adjustments.luminancePurple = Math.max(
			(adjustments.luminancePurple ?? 0) - hueDeltaPurple * 2,
			-0.4,
		)

		logColorMatch("[correctWhiteColor] correct blue hue", {
			tempChange: deltaTemp,
			hueDeltaBlue,
			hueDeltaPurple,
			currBlueHue,
		})
	}

	// --- Step 5b: Saturation compensation ---
	// Lowering temperature can desaturate warm tones, making faces look pale.
	// Partially recover global saturation proportional to the temp decrease.
	if (correctSaturation && deltaTemp < 0) {
		const deltaTempScale = clamp(1 + deltaTemp / 600, 0.2, 1.0)
		const currSaturation = adjustments.saturation ?? 0
		if (!isCloseToBw && currSaturation < -0.04) {
			adjustments.saturation = getDecreasedValue(currSaturation, 0.04, deltaTempScale)
		}
	}

	return adjustments
}

function getBlacks(
	grayCurveBefore: Float32Array,
	grayCurveAfter: Float32Array,
	limitLow: number = -0.4,
	limitHigh: number = 0.6,
): number {
	const diffLow = getMeanDifference(grayCurveAfter, grayCurveBefore, 0, 70)
	const blacks =
		diffLow < 0
			? clamp(diffLow / 0.19, limitLow, limitHigh)
			: clamp(diffLow / 0.07, limitLow, limitHigh)

	return blacks
}

function getWhites(
	grayCurveBefore: Float32Array,
	grayCurveAfter: Float32Array,
	limitLow: number = -0.4,
	limitHigh: number = 0.6,
): number {
	const diffHigh = getMeanDifference(grayCurveAfter, grayCurveBefore, 190, 235)
	const whites =
		diffHigh < 0
			? clamp(diffHigh / 0.12, limitLow, limitHigh)
			: clamp(diffHigh / 0.2, limitLow, limitHigh)

	return whites
}

function getShadows(
	grayCurveBefore: Float32Array,
	grayCurveAfter: Float32Array,
	limitLow: number = -0.5,
	limitHigh: number = 0.8,
): number {
	const diffLow = getMeanDifference(grayCurveAfter, grayCurveBefore, 10, 100)
	const shadows =
		diffLow < 0
			? clamp(diffLow / 0.13, limitLow, limitHigh)
			: clamp(diffLow / 0.21, limitLow, limitHigh)

	return shadows
}

function getHighlights(
	grayCurveBefore: Float32Array,
	grayCurveAfter: Float32Array,
	limitLow: number = -0.8,
	limitHigh: number = 0.5,
): number {
	const diffHigh = getMeanDifference(grayCurveAfter, grayCurveBefore, 170, 256)
	const highlights =
		diffHigh < 0
			? clamp(diffHigh / 0.21, limitLow, limitHigh)
			: clamp(diffHigh / 0.14, limitLow, limitHigh)

	return highlights
}

function getHighlightsShadows(options: {
	prophotoCurrent: Float32Array
	gammaAfter: Float32Array
	srgbBefore: Float32Array
	adjustments: Partial<Adjustments>
	baseRenderOptions: RenderOptions
	lightingLimits: LightingLimits
}): Partial<Adjustments> {
	const {
		prophotoCurrent,
		gammaAfter,
		srgbBefore,
		adjustments,
		baseRenderOptions,
		lightingLimits,
	} = options
	const {
		minShadows,
		maxShadows,
		minBlacks,
		maxBlacks,
		minHighlights,
		maxHighlights,
		minWhites,
		maxWhites,
	} = lightingLimits

	let gammaCurrent = applyTransform(prophotoCurrent, linearToGamma)
	let grayCurveCurrent = getGrayImage(gammaCurrent)

	const grayCurveAfter = getGrayImage(gammaAfter)

	const initialDiffAll = getMeanAbsDifference(grayCurveAfter, grayCurveCurrent)
	logColorMatch("[highlight/shadow] initial diff_all", initialDiffAll)

	const adjustmentsCopy = { ...adjustments }
	let diffAll = 1
	let lastDiffAll = 1
	let iterations = 0

	let finalAdjustments: Partial<Adjustments> = { ...adjustments }
	let shadows = adjustments.shadows ?? 0
	let highlights = adjustments.highlights ?? 0
	let blacks = adjustments.blacks ?? 0
	let whites = adjustments.whites ?? 0

	const scaleHighlightsShadows = 0.5

	while (Math.abs(diffAll) > 0.005) {
		const scaleBlacksWhites = iterations > 0 ? 0.4 : 0.0
		const shadowsDelta = getShadows(grayCurveCurrent, grayCurveAfter) * scaleHighlightsShadows
		const highlightsDelta =
			getHighlights(grayCurveCurrent, grayCurveAfter) * scaleHighlightsShadows
		const blacksDelta = getBlacks(grayCurveCurrent, grayCurveAfter) * scaleBlacksWhites
		const whitesDelta = getWhites(grayCurveCurrent, grayCurveAfter) * scaleBlacksWhites

		shadows = clamp(shadows + shadowsDelta, minShadows, maxShadows)
		highlights = clamp(highlights + highlightsDelta, minHighlights, maxHighlights)
		blacks = clamp(blacks + blacksDelta, minBlacks, maxBlacks)
		whites = clamp(whites + whitesDelta, minWhites, maxWhites)

		adjustmentsCopy.shadows = shadows
		adjustmentsCopy.highlights = highlights
		adjustmentsCopy.blacks = blacks
		adjustmentsCopy.whites = whites

		gammaCurrent = renderCpu(srgbBefore, adjustmentsCopy, {
			...baseRenderOptions,
			outColorSpace: ColorSpaceCpu.gamma,
		})
		grayCurveCurrent = getGrayImage(gammaCurrent)

		diffAll = getMeanAbsDifference(grayCurveAfter, grayCurveCurrent)
		logColorMatch("[highlight/shadow] diff_all", diffAll, ", iterations:", iterations)
		logColorMatch("[highlight/shadow] deltas", {
			shadowsDelta,
			highlightsDelta,
			blacksDelta,
			whitesDelta,
		})

		if (
			Math.abs(diffAll) > Math.abs(lastDiffAll) - 0.0001 ||
			iterations > 5 ||
			Math.abs(diffAll) < 0.005
		) {
			break
		}
		lastDiffAll = diffAll
		iterations++

		finalAdjustments = { ...adjustmentsCopy }
	}

	return finalAdjustments
}

export function getSaturation(options: {
	prophotoCurrent: Float32Array
	prophotoAfter: Float32Array
	toneMapInvBefore: Float32Array
	adjustments: Partial<Adjustments>
	baseRenderOptions: RenderOptions
	pixelWeights: number[]
	iterations: number
	maxLimit: number
	minLimit?: number
}): Partial<Adjustments> {
	const {
		prophotoAfter,
		toneMapInvBefore,
		adjustments,
		baseRenderOptions,
		pixelWeights,
		iterations,
		maxLimit,
	} = options
	const minLimit = options.minLimit ?? -1
	let prophotoCurrent = options.prophotoCurrent

	const satAfter = getWeightedMean(
		applyTransformToOneDimension(prophotoAfter, rgbToSat),
		pixelWeights,
	)

	const renderOptions = {
		...baseRenderOptions,
		outColorSpace: ColorSpaceCpu.proPhoto,
		inputColorSpace: ColorSpaceCpu.toneMapInversed,
	}

	// Calculate maximum possible saturation
	const adjustmentsMax = { ...adjustments, saturation: 1.0 }
	const prophotoMax = renderCpu(toneMapInvBefore, adjustmentsMax, renderOptions)
	const satMax = getWeightedMean(
		applyTransformToOneDimension(prophotoMax, rgbToSat),
		pixelWeights,
	)

	const initialError = getWeightedToneDiff(prophotoCurrent, prophotoAfter, pixelWeights)
	let lastError = initialError
	logColorMatch(
		"[saturation] initial error",
		initialError,
		", satAfter",
		satAfter,
		", satMax",
		satMax,
	)

	let result = adjustments
	for (let i = 0; i < iterations; i++) {
		const satCurrent = getWeightedMean(
			applyTransformToOneDimension(prophotoCurrent, rgbToSat),
			pixelWeights,
		)
		let satAdj: number
		if (satAfter > satCurrent) {
			satAdj = (satAfter - satCurrent) / Math.max(satMax - satCurrent, 0.0001)
		} else {
			satAdj = -(satCurrent - satAfter) / Math.max(satCurrent, 0.0001)
		}
		const scale = iterations === 0 ? 0.6 : 1.0
		const currSaturation = result.saturation ?? 0
		const space = satAdj > 0 ? 1 - currSaturation : 1 + currSaturation
		const saturation = clamp(currSaturation + satAdj * scale * space, minLimit, maxLimit)
		const satDiff = Math.abs(saturation - currSaturation)
		if (satDiff <= 0.01) {
			break
		}
		const tempAdjustments = { ...result, saturation }
		prophotoCurrent = cachedRenderCpu(toneMapInvBefore, tempAdjustments, renderOptions)
		const error = getWeightedToneDiff(prophotoCurrent, prophotoAfter, pixelWeights)
		logColorMatch("[saturation]", { iteration: i, error, satAdj, satCurrent, saturation })
		if ((error >= lastError && i > 0) || (i === 0 && error > lastError + 0.002)) {
			break
		}
		lastError = error
		result = tempAdjustments
		if (satDiff <= 0.02) {
			break
		}
	}

	if (lastError > initialError) {
		return adjustments
	}

	return result
}

/**
 * Half color match path corrections, called from within adjustFaceTone
 * (between face-to-LUT alignment and fine-grained face HSL targeting).
 *
 * Skipped for full color match (no baseAdjustments) — those corrections
 * run directly in estimate full color match parameters instead.
 *
 * 1. Vegetation color: WB + green/yellow HSL correction (only when the
 *    preset has no base WB, i.e. temperature is undefined)
 * 2. Saturation compensation: when lightStrength < 1 (reduced light
 *    transfer), the lighting darkening may be strong causing the saturation to be too high.
 */
export function applyCorrection(options: {
	adjustments: Partial<Adjustments>
	baseRenderOptions: RenderOptions
	colorStrength: number
	srgbBefore: Float32Array
	pixelWeights: number[]
	referenceFeatures: ReferenceColorMatchFeatures
	lightStrength: number
	inputPreset: Float32Array
	inputAfter: Float32Array
	luts?: NeuralColorMatchResult
	baseAdjustments: Partial<Adjustments> | undefined
}) {
	const {
		adjustments,
		baseRenderOptions,
		colorStrength,
		srgbBefore,
		pixelWeights,
		referenceFeatures,
		lightStrength,
		inputPreset,
		inputAfter,
		luts,
		baseAdjustments,
	} = options

	// if no base adjustments (full color match path), return the adjustments as is
	if (!baseAdjustments) {
		return adjustments
	}

	const asShotWB = {
		asShotTemp: baseRenderOptions.asShotTemp,
		asShotTint: baseRenderOptions.asShotTint,
	}

	let result = { ...adjustments }

	// wb+HSL vegetation color correction only for key photo with no base wb adjustments
	if (baseAdjustments.temperature == undefined) {
		result = correctVegetation({
			adjustments: result,
			srgbBefore: srgbBefore,
			pixelWeights,
			asShotWB,
			baseRenderOptions,
			referenceFeatures,
			hasBaseAdjustments: true,
			colorStrength,
		})
	}

	// correct saturation due to reduced light strength if needed
	if (lightStrength < 1 && luts) {
		result = correctSaturationDueToReducedLightStrength({
			adjustments: result,
			inputPreset,
			inputAfter,
			pixelWeights,
			luts,
			colorStrength,
			baseSaturation: baseAdjustments.saturation ?? 0,
		})
	}

	return result
}

function applyHighlightsCorrection(options: {
	adjustments: Partial<Adjustments>
	baseRenderOptions: RenderOptions
	pixelWeights: number[]
	toneMapInvBefore: Float32Array
	maxExposure: number
}) {
	const { adjustments, baseRenderOptions, pixelWeights, toneMapInvBefore, maxExposure } = options
	const srgbCurrent = cachedRenderCpu(toneMapInvBefore, adjustments, {
		...baseRenderOptions,
		inputColorSpace: ColorSpaceCpu.toneMapInversed,
		outColorSpace: ColorSpaceCpu.sRGB,
	})
	const autoHighlights = getAutoHighlights(srgbCurrent, pixelWeights)
	logColorMatch("!!autoHighlightsDelta", autoHighlights)

	if (autoHighlights < 0) {
		const initialHighlights = adjustments.highlights ?? 0
		let highlights = initialHighlights + autoHighlights * 0.9
		if (highlights < -0.5) {
			let limit = 0.5
			if (initialHighlights < 0) {
				limit = Math.max(limit, -initialHighlights)
			}
			highlights = getDecreasedValue(highlights, limit, 0.5)
		}
		const deltaHighlights = clamp(highlights, -1, 1) - (adjustments.highlights ?? 0)
		adjustments.highlights = clamp(highlights, -1, 1)

		const initialWhites = adjustments.whites ?? 0
		let whites = initialWhites + autoHighlights
		if (whites < -0.5) {
			let limit = 0.5
			if (initialWhites < 0) {
				limit = Math.max(limit, -initialWhites)
			}
			whites = getDecreasedValue(whites, limit, 0.5)
		}
		const deltaWhites = clamp(whites, -1, 1) - (adjustments.whites ?? 0)
		adjustments.whites = clamp(whites, -1, 1)

		const deltaBrighten = -(deltaHighlights + deltaWhites) * 0.3
		const exposure = adjustments.exposure ?? 0
		if (deltaBrighten > 0) {
			const maxDeltaExposure = Math.max(0, maxExposure - 0.15 - exposure)
			const deltaExposure = Math.min(maxDeltaExposure, deltaBrighten)
			const deltaShadows = deltaBrighten - deltaExposure
			adjustments.exposure = Math.min(maxExposure, exposure + deltaExposure)
			adjustments.shadows = clamp((adjustments.shadows ?? 0) + deltaShadows, -1, 1)
			logColorMatch("!!correctedHighlightsWhites exposure", {
				deltaExposure,
				deltaShadows,
			})
		}

		logColorMatch("!!correctedHighlightsWhites", {
			deltaHighlights,
			deltaWhites,
			initialWhites,
			initialHighlights,
		})
	}

	return adjustments
}

function adjustWithAutoLightParams(options: {
	adjustments: Partial<Adjustments>
	inputBefore: Float32Array
	pixelWeights: number[]
	baseRenderOptions: RenderOptions
	lightStrength: number
	isDark: boolean
}) {
	const { adjustments, inputBefore, pixelWeights, baseRenderOptions, lightStrength, isDark } =
		options
	const currentSrgb = renderCpu(inputBefore, adjustments, {
		inputColorSpace: ColorSpaceCpu.sRGB,
		outColorSpace: ColorSpaceCpu.sRGB,
		...baseRenderOptions,
	})
	const percentiles = getLuminancePercentiles(
		currentSrgb,
		pixelWeights,
		[0.05, 0.2, 0.5, 0.75, 0.9, 0.95, 0.99],
		false,
	)
	logColorMatch("!!adjustWithAutoLightParams percentiles", percentiles)
	const lumStats = {
		avgLum: percentiles[2] as number,
		shadowsMean:
			((percentiles[0] as number) + (percentiles[1] as number) + (percentiles[2] as number)) *
			0.33,
		clippingPercent: 0,
		shadowsClippingPercent: 0,
		percentiles: percentiles,
		facePercentiles: [],
		facePercent: 0,
		faceLum: 0,
		faceCount: 0,
	}
	const autoLightParams = getAutoExposure(lumStats)
	const strength = 1 - step(lightStrength, 0.6, 1)

	// if reference photo is not dark (too bright), the algorithm tends to over exposure so don't add exposure/shadows delta
	if (!isDark) {
		autoLightParams.shadowsDelta = 0
		autoLightParams.contrastDelta = 0
		autoLightParams.exposureDelta = Math.min(autoLightParams.exposureDelta, 0.0)
		autoLightParams.blacksDelta = 0
	}

	adjustments.exposure = (adjustments.exposure ?? 0) + autoLightParams.exposureDelta * strength
	adjustments.highlights =
		(adjustments.highlights ?? 0) + (autoLightParams.highlightsDelta / 100) * strength
	adjustments.shadows =
		(adjustments.shadows ?? 0) + (autoLightParams.shadowsDelta / 100) * strength
	adjustments.whites = (adjustments.whites ?? 0) + (autoLightParams.whitesDelta / 100) * strength
	adjustments.blacks = (adjustments.blacks ?? 0) + (autoLightParams.blacksDelta / 100) * strength
	adjustments.contrast =
		(adjustments.contrast ?? 0) + (autoLightParams.contrastDelta / 100) * strength

	logColorMatch("[adjustWithAutoLightParams] auto light params", {
		autoLightParams,
		strength,
		lightStrength,
	})

	return adjustments
}

/**
 * Computes lightStrength (0.6–1) — controls how much of the LUT's lighting
 * effect is applied via transformImage(). Reduced when:
 *
 * - Face would be over-brightened (lightStrengthFace): caps face luminance
 *   relative to input, especially for dark skin tones
 * - Reference is very dark (refLum < 0.35) or has clipped highlights
 * - Luminance distribution differs significantly between input and reference
 *
 * Final lightStrength = min(overall, face). Used alongside colorStrength
 * (see getFeatureDistAndColorStrength in processing-utils.ts).
 */
function getLightStrength(options: {
	inputFeatures: InputColorMatchFeaturesWithFaceData
	referenceFeatures: ReferenceColorMatchFeatures
	colorStrength: number
	faceColorsWithPreset: Float32Array | null
	luts: NeuralColorMatchResult
}) {
	const { inputFeatures, referenceFeatures, colorStrength, faceColorsWithPreset, luts } = options

	let lightStrengthFace = 1
	if (faceColorsWithPreset?.length && inputFeatures.faceWeights?.length) {
		const faceColorsAfterFullLight = transformImage({
			image: faceColorsWithPreset,
			lut1d: luts.lut1d,
			lut3d: luts.lut3d,
			colorStrength,
			lightStrength: 1,
			originalInput: undefined,
		})
		const afterFaceHSVL = getFaceHSVL(faceColorsAfterFullLight, inputFeatures.faceWeights)

		const afterLum = afterFaceHSVL[3]
		const inputLum = inputFeatures.avgFaceHsvl?.[3] ?? 0
		// inputLum + 0.4 is to prevent lighting up face too much especially for dark skin
		let maxLum = Math.min(inputLum + 0.4, Math.max(0.68, inputLum + 0.1))
		if (isFaceProblematic(inputFeatures.avgFaceHsvl)) {
			// for problematic face, we can relax the max lum to at least 0.62
			maxLum = Math.max(maxLum, 0.62)
		}
		if (afterLum > maxLum) {
			lightStrengthFace = clamp((maxLum - inputLum) / (afterLum - inputLum), 0.6, 1)
		}

		logColorMatch("[getLightStrength] lightStrengthFace", {
			afterFaceHSVL,
			inputFaceHSVL: inputFeatures.avgFaceHsvl,
			lightStrengthFace,
		})
	}

	let lightStrength = 1
	const refLumStats = referenceFeatures.satLumStats
	const refLum = refLumStats?.lumPercentiles[2] ?? refLumStats?.totalAvgLum
	const lightDist = meanAbsoluteDifference(
		inputFeatures.lumPercentiles ?? [],
		refLumStats?.lumPercentiles ?? [],
	)
	const refHighlights = getAutoHighlightsFromPercentiles(refLumStats?.lumPercentiles ?? [])
	logColorMatch("[getLightStrength] ref stats", { refHighlights, refLumStats })
	if (refLum != undefined && (refLum < 0.35 || (refHighlights < -0.06 && refLum > 0.5))) {
		const inputLum = inputFeatures.lumPercentiles?.[2] ?? refLum
		let tooDarkStrength = step(refLum, 0.05, 0.35)
		if (inputLum < refLum) {
			tooDarkStrength = 1
		}
		let tooLightStrength = step(refHighlights, -0.75, -0.06)
		if (inputLum > refLum) {
			tooLightStrength = 1
		}
		const distanceStrength = 1 - step(lightDist, 0.1, 0.75)
		lightStrength = Math.max(
			Math.min(tooDarkStrength, tooLightStrength),
			Math.min(distanceStrength, colorStrength),
			0.6,
		)
		if (refLum > 0.35) {
			// if ref is too bright, set minimum light strength to 0.7
			lightStrength = Math.max(lightStrength, 0.7)
		}
		logColorMatch("[getLightStrength] overall", {
			tooDarkStrength,
			tooLightStrength,
			distanceStrength,
			lightStrength,
		})
	}

	const result = Math.min(lightStrength, lightStrengthFace)
	logColorMatch("[getLightStrength] final result", result)
	return result
}

/**
 * Sometimes half color match intentionally applies the LUT at reduced lightStrength
 * (partial light transfer). This makes the result darker than the full-LUT
 * output, which perceptually inflates saturation. Scale back the extra
 * saturation (above base preset) proportionally to the luminance drop.
 */
function correctSaturationDueToReducedLightStrength(options: {
	adjustments: Partial<Adjustments>
	inputPreset: Float32Array
	inputAfter: Float32Array
	pixelWeights: number[]
	luts: NeuralColorMatchResult
	colorStrength: number
	baseSaturation: number
}) {
	const {
		adjustments,
		colorStrength,
		inputAfter,
		inputPreset,
		luts,
		pixelWeights,
		baseSaturation,
	} = options

	const currentSaturation = adjustments.saturation ?? 0
	if (currentSaturation <= baseSaturation) {
		return adjustments
	}

	const inputAfterFullLight = transformImage({
		image: inputPreset,
		lut1d: luts.lut1d,
		lut3d: luts.lut3d,
		colorStrength,
		lightStrength: 1,
		originalInput: undefined,
	})

	const meanLum = getWeightedMedianLuminance(inputAfter, pixelWeights)
	const meanLumFullLight = getWeightedMedianLuminance(inputAfterFullLight, pixelWeights)
	const lumDeltaPercent = (meanLumFullLight - meanLum) / meanLum

	const extraSaturation = currentSaturation - baseSaturation
	const lumScale = clamp(step(0.3 - lumDeltaPercent, 0, 0.25), 0, 1)
	adjustments.saturation = clamp(baseSaturation + extraSaturation * lumScale, -1, 1)

	logColorMatch("[correctSaturationDueToReducedLightStrength]", {
		extraSaturation,
		meanLum,
		meanLumFullLight,
		lumDeltaPercent,
		lumScale,
	})

	return adjustments
}
